import {
    Component,
    ChangeDetectionStrategy,
    Input,
    ViewChild,
    Type,
    Output,
    EventEmitter,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MarkerComponent } from '@maplibre/ngx-maplibre-gl';
import { MapItem, MapItemComponent } from '@ui/shared/util-map';
import { PopupContentDirective } from '../../directives/popup-content.directive';
import { SelectedItemState } from '@ui/shared/domain-map';
import { Observable, map } from 'rxjs';
import { MapEventPropagationService } from '../../services/map-event-propagation.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-map-marker-with-popup',
    templateUrl: './map-marker-with-popup.component.html',
    styleUrls: ['./map-marker-with-popup.component.scss'],
    standalone: false,
})
export class MapMarkerWithPopupComponent<MapItemType extends MapItem> {
    @ViewChild(PopupContentDirective, { static: true })
    public uiPopupContentHost?: PopupContentDirective;

    @ViewChild(MarkerComponent)
    public markerComponent?: MarkerComponent;

    @Input({ required: true })
    public mapItem!: MapItemType;

    @Input()
    public isArea?: boolean;

    @Input({ required: true })
    public popupContentComponentType!: Type<MapItemComponent<MapItemType>>;

    /*
     * The marker can be hidden from view.
     * To preserve functionality, this only hides the marker icon but keeps the popup and the functional component.
     */
    @Input()
    public hidden = true;

    @Output()
    public readonly markerClicked = new EventEmitter<MapItemType>();

    @Output()
    public readonly markerPopupOpened = new EventEmitter<MapItemType>();

    @Output()
    public readonly markerPopupClosed = new EventEmitter<MapItemType>();

    public isThisMapItemSelected$: Observable<boolean>;

    private _popupOpen = false;

    constructor(
        private readonly _selectedMapItemService: SelectedItemState<MapItemType>,
        private readonly _mapEventPropagationService: MapEventPropagationService,
    ) {
        this.isThisMapItemSelected$
            = this._selectedMapItemService.mapItem$
                .pipe(
                    map(mapItem => mapItem?.uniqueId === this.mapItem.uniqueId),
                );

        this._selectedMapItemService.mapItem$
            .pipe(
                takeUntilDestroyed(),
            )
            .subscribe(
                this._handleSelectedItemChanged.bind(this),
            );
    }

    public handleMarkerClicked(mouseEvent?: Event): void {
        this._popupOpen = !this._popupOpen;

        if (mouseEvent) {
            this._mapEventPropagationService.registerEvent(mouseEvent);
        }

        if (this._selectedMapItemService.getMapItem() !== this.mapItem) {
            this._selectedMapItemService.setMapItem(this.mapItem);
        } else {
            this._selectedMapItemService.setMapItem(null);
        }
    }

    public handlePopupClose(): void {
        this._popupOpen = false;

        if (this._selectedMapItemService.getMapItem()?.uniqueId === this.mapItem.uniqueId) {
            this._selectedMapItemService.setMapItem(null);
        }

        this.markerPopupClosed.emit(this.mapItem);
    }

    public handlePopupOpen(): void {
        this._popupOpen = true;
        this._loadPopupContent();
        this.markerPopupOpened.emit(this.mapItem);
    }

    private _handleSelectedItemChanged(mapItem: MapItem | null): void {
        if (mapItem !== this.mapItem) {
            this._closePopup();
        } else {
            this._openPopup();
        }
    }

    private _openPopup(): void {
        const marker = this.markerComponent?.markerInstance();

        if (marker && !this._popupOpen) {
            this._popupOpen = true;
            marker.togglePopup();
            this.markerPopupOpened.emit(this.mapItem);
        }
    }

    private _closePopup(): void {
        const marker = this.markerComponent?.markerInstance();

        if (marker && this._popupOpen) {
            this._popupOpen = false;
            marker.togglePopup();
        }
    }

    private _loadPopupContent(): void {
        if (!this.uiPopupContentHost) {
            return;
        }

        const viewContainerRef = this.uiPopupContentHost.viewContainerRef;

        viewContainerRef.clear();

        const componentRef = viewContainerRef.createComponent(this.popupContentComponentType);

        componentRef.setInput('mapItem', this.mapItem);
    }
}
